import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Loader2, Download, Mail, Phone, Sparkles } from "lucide-react";

const API = `${API_BASE}/api`;

/**
 * PublicProposalSharePage — landing page a rep's prospect lands on when they
 * click the emailed share link. No auth required. Shows deck metadata + a
 * big Download PPTX button. Rep contact info is surfaced so the prospect
 * has an obvious next step.
 */
export default function PublicProposalSharePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  useEffect(() => {
    if (!token) { setStatus("error"); return; }
    axios.get(`${API}/public/prospect-proposals/${token}`)
      .then(r => { setMeta(r.data); setStatus("ok"); })
      .catch(() => setStatus("error"));
  }, [token]);

  const download = () => {
    window.location.href = `${API}/public/prospect-proposals/${token}/download`;
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-fuchsia-600" />
      </div>
    );
  }
  if (status === "error" || !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-3xl mb-2">🔒</div>
          <h1 className="text-xl font-bold mb-2">Proposal not available</h1>
          <p className="text-sm text-slate-600">
            This link may have expired or been revoked. Please contact your Ready Prospect partner for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  const created = meta.created_at ? new Date(meta.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-fuchsia-50 to-indigo-50" data-testid="public-proposal-page">
      <div className="max-w-3xl mx-auto p-6 py-16">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header band */}
          <div className="bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white p-8">
            <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-fuchsia-100 mb-2">
              <Sparkles className="h-4 w-4" />
              Prospect IQ Proposal
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold" data-testid="proposal-business-name">
              {meta.business_name || meta.client_name}
            </h1>
            {meta.agency_name && (
              <p className="text-fuchsia-100 mt-2 text-sm">Prepared by {meta.agency_name}</p>
            )}
            {created && <p className="text-fuchsia-200 mt-1 text-xs">{created}</p>}
          </div>

          {/* Body */}
          <div className="p-8 space-y-6">
            <p className="text-slate-700 leading-relaxed">
              Your customized marketing proposal is ready to review.
              Click below to download the full presentation deck (PowerPoint format).
              You can open it in PowerPoint, Google Slides, or Keynote.
            </p>

            <Button
              onClick={download}
              size="lg"
              data-testid="proposal-download-btn"
              className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold py-6 text-base"
            >
              <Download className="h-5 w-5 mr-2" /> Download Proposal (PPTX)
            </Button>

            {(meta.rep_name || meta.rep_email || meta.rep_phone) && (
              <div className="border-t pt-6">
                <p className="text-xs font-bold tracking-widest uppercase text-slate-500 mb-3">
                  Questions? Reach out to your partner
                </p>
                <div className="space-y-1.5">
                  {meta.rep_name && (
                    <p className="font-semibold text-slate-900" data-testid="rep-name">
                      {meta.rep_name}
                    </p>
                  )}
                  {meta.rep_email && (
                    <a
                      href={`mailto:${meta.rep_email}`}
                      className="flex items-center gap-2 text-sm text-fuchsia-700 hover:underline"
                      data-testid="rep-email-link"
                    >
                      <Mail className="h-4 w-4" /> {meta.rep_email}
                    </a>
                  )}
                  {meta.rep_phone && (
                    <a
                      href={`tel:${meta.rep_phone}`}
                      className="flex items-center gap-2 text-sm text-slate-600 hover:underline"
                      data-testid="rep-phone-link"
                    >
                      <Phone className="h-4 w-4" /> {meta.rep_phone}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          Secured link · powered by Ready Prospect
        </p>
      </div>
    </div>
  );
}
